import { useEffect, useState, useCallback } from 'react';
import { Header } from '@/components/shared/Header';
import { DisclaimerModal } from '@/components/modals/DisclaimerModal';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { FeedbackForm, FeedbackButton } from '@/components/FeedbackForm/FeedbackForm';
import type { UserFeedback } from '@/utils/userFeedback';
import { router } from './router';
import { useStore } from '@/store/useStore';
import { useAppLock } from '@/components/AppLock';
import { setIDBAdapter, resetIDBAdapter } from '@/persistence/db';

/**
 * App shell: Header + CdsNotice; gates on disclaimer ack → modal else router.
 * Loads settings + runs initial computeAll on mount (TODO Task 1.18).
 * Wires AppLock IDB encryption adapter on unlock/lock transitions.
 * Renders floating feedback widget + feedback modal.
 */
export default function App(): JSX.Element {
  const computeAll = useStore((s) => s.computeAll);
  const { isUnlocked, lock } = useAppLock();

  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackConfig, setFeedbackConfig] = useState<{
    type?: UserFeedback['type'];
    category?: string;
    error?: string;
  }>({});

  const handleOpenFeedback = useCallback(
    (type?: UserFeedback['type'], category?: string, error?: string) => {
      setFeedbackConfig({ type, category, error });
      setFeedbackOpen(true);
    },
    [],
  );

  const handleCloseFeedback = useCallback(() => {
    setFeedbackOpen(false);
    setFeedbackConfig({});
  }, []);

  useEffect(() => {
    computeAll();
  }, [computeAll]);

  useEffect(() => {
    if (isUnlocked && lock?.idb) {
      setIDBAdapter(lock.idb);
    } else {
      resetIDBAdapter();
    }
  }, [isUnlocked, lock]);

  return (
    <ErrorBoundary
      label="Application"
      onReportError={(msg) => handleOpenFeedback('bug_report', 'bug', msg)}
    >
      <DisclaimerModal />
      <Header />
      <main className="app-main">
        <ErrorBoundary
          label="Screen"
          onReportError={(msg) => handleOpenFeedback('bug_report', 'bug', msg)}
        >
          {router()}
        </ErrorBoundary>
      </main>
      <footer className="app-footer">
        <span>Amino PK · Offline-first · v{VITE_APP_VERSION}</span>
      </footer>

      <FeedbackForm
        isOpen={feedbackOpen}
        onClose={handleCloseFeedback}
        initialType={feedbackConfig.type}
        initialCategory={feedbackConfig.category}
        initialError={feedbackConfig.error}
      />
      <FeedbackButton onOpen={handleOpenFeedback} />
    </ErrorBoundary>
  );
}
